/* eslint-disable @typescript-eslint/no-shadow */
import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { OktaRestriction } from 'src/app/core/services/okta-restrictions.service';
import { WebSocketService } from 'src/app/core/services/web-socket.service';
import { Campaign } from 'src/app/models/campaign';
import { ACTIONS, STATUSES } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from '../../../shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { LoadContent } from '../../content/redux/content.actions';
import { ContentState } from '../../content/redux/content.state';
import { InsightsState, LoadInsights } from '../../insights/redux';
import { SurveysState } from '../../surveys/redux';
import { LoadSurveys } from '../../surveys/redux/surveys.actions';
import { CampaignState, LoadCampaign, ReloadCampaign } from '../redux';
import { CampaignService } from '../services/campaign.service';
import { CampaignTerminateModalComponent } from './campaign-terminate-modal/campaign-terminate-modal.component';
import { DeleteCampaignComponent } from './delete-campaign/delete-campaign.component';
import { EditCampaignComponent } from './edit-campaign/edit-campaign.component';
import { StatsCampaignComponent } from './stats-campaign/stats-campaign.component';
import { SharedModule } from '../../../shared/shared.module';

@Component({
    selector: 'app-campaigns',
    changeDetection: ChangeDetectionStrategy.Default,
    templateUrl: './campaigns.component.html',
    styleUrls: ['./campaigns.component.css'],
    providers: [DecimalPipe],
    imports: [SharedModule, RouterOutlet]
})
export class CampaignsComponent implements OnInit, OnDestroy {
  public campaign$: Observable<any[]> = inject(Store).select(CampaignState.campaign);
  public campaignJob$: Observable<any[]> = inject(Store).select(CampaignState.campaignJobs);
  private unsubscribe: Subject<void> = new Subject();

  public tableData;
  public total$: Observable<number>;
  public showActions = [];
  public campaignJobs;

  constructor(
    private service: CampaignService,
    private store: Store,
    private dialogService: NbDialogService,
    private router: Router,
    private socketService: WebSocketService,
    private toastrService: NbToastrService,
    private restrictionService: OktaRestriction
  ) {
    this.store.dispatch(LoadCampaign);
    if (!this.store.selectSnapshot(InsightsState.insightsActive)) {
      this.store.dispatch(LoadInsights);
    }
    if (!this.store.selectSnapshot(ContentState.content)) {
      this.store.dispatch(LoadContent);
    }
    if (!this.store.selectSnapshot(SurveysState.surveys)) {
      this.store.dispatch(LoadSurveys);
    }
    if (this.restrictionService.manager_permission) {
      this.showActions = [
        ACTIONS.CREATE,
        ACTIONS.STATS,
        ACTIONS.START,
        ACTIONS.STOP,
        ACTIONS.EDIT,
        ACTIONS.CLONE,
        ACTIONS.DELETE,
        ACTIONS.FAVORITE
      ];
    }
  }

  public ngOnInit() {
    this.campaign$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
    this.campaignJob$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.campaignJobs = data));
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.tableData = undefined;
      this.store.dispatch(ReloadCampaign);
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditCampaignComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          campaign: new Campaign()
        }
      });
    }
    if ($event.action === ACTIONS.CLONE) {
      const clonedCampaign = Campaign.clone($event.data);
      clonedCampaign.id = null;
      this.dialogService.open(EditCampaignComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          campaign: clonedCampaign
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const campaign: Campaign = $event.data;
      this.router.navigate([`/campaigns/table/` + campaign.id]);
    }
    if ($event.action === 'stats') {
      const campaign: Campaign = $event.data;
      this.dialogService.open(StatsCampaignComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          campaign
        }
      });
    }
    if ($event.action === ACTIONS.START) {
      if ($event.data.status === STATUSES.RUNNING) {
        this.toastrService.show(status, $localize`Cannot start a running campaign`, { status: 'danger' });
      } else {
        const campaign: Campaign = $event.data;
        this.dialogService
          .open(NgxGenericModalComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              title: $localize`Are you sure you want to "Run": `,
              message: campaign.id
            }
          })
          .onClose.subscribe((data) => {
            if (data) {
              this.socketService.setUp();
              this.service
                .runCampaignJob(campaign)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe((data) => {
                  setTimeout(() => {
                    this.socketService.sendMessage(data.job_id, campaign.id);
                  }, 2000);
                });
            }
          });
      }
    }
    if ($event.action === ACTIONS.STOP) {
      if ($event.data.status !== STATUSES.RUNNING) {
        this.toastrService.show(status, $localize`Campaign is not running`, { status: 'danger' });
      } else {
        const campaign: Campaign = $event.data;
        this.dialogService.open(CampaignTerminateModalComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: { campaign }
        });
      }
    }
    if ($event.action === ACTIONS.EDIT) {
      if ($event.data.status === STATUSES.RUNNING) {
        this.toastrService.show(status, $localize`Cannot edit a running campaign`, { status: 'danger' });
      } else {
        const campaign: Campaign = $event.data;
        this.dialogService.open(EditCampaignComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            action: $event.action,
            campaign: Campaign.copy(campaign) as Campaign
          }
        });
      }
    }
    if ($event.action === ACTIONS.DELETE) {
      const campaign: Campaign = $event.data;
      this.dialogService.open(DeleteCampaignComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          campaign: Campaign.copy(campaign) as Campaign
        }
      });
    }
  }
}
